// Local-only wishlist
//
// Stored in localStorage as an array of Shopify product handles. We expose a
// React hook with cross-tab sync (storage event) and an in-memory pubsub so
// multiple components stay in step in the same tab.
//
// Why handles, not full products: products change/sell, but a handle stays
// stable, and we re-fetch fresh data on the wishlist page anyway.

import { useEffect, useState, useCallback } from "react";
import { trackWishlistAdd } from "@/lib/analytics";

const KEY = "fl:wishlist:v1";
type Listener = (handles: string[]) => void;
const listeners = new Set<Listener>();

function read(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function write(handles: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(handles));
  } catch {
    // quota / private mode — ignore
  }
  listeners.forEach((l) => l(handles));
}

export function useWishlist() {
  const [handles, setHandles] = useState<string[]>(() => read());

  useEffect(() => {
    const onChange: Listener = (next) => setHandles(next);
    listeners.add(onChange);
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY) setHandles(read());
    };
    window.addEventListener("storage", onStorage);
    // Ensure SSR/client diff resolves to the real value
    setHandles(read());
    return () => {
      listeners.delete(onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const has = useCallback((handle: string) => handles.includes(handle), [handles]);

  const toggle = useCallback((handle: string) => {
    const current = read();
    const adding = !current.includes(handle);
    const next = adding ? [handle, ...current] : current.filter((h) => h !== handle);
    write(next);
    if (adding) {
      trackWishlistAdd({ id: handle, handle, title: handle });
    }
  }, []);

  const remove = useCallback((handle: string) => {
    const current = read();
    write(current.filter((h) => h !== handle));
  }, []);

  const clear = useCallback(() => write([]), []);

  return { handles, has, toggle, remove, clear, count: handles.length };
}
